import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../features/axiosHelper";
import { Box, Button, Card, Container, Typography } from "@mui/material";
import MainNavigation from "@/components/MainNavigation/MainNavigation";
import CardDataGrid, {
  GridLogicOperator,
  RenderStatus,
} from "@/components/DataGrid/CardDataGrid";
import { Link } from "react-router-dom";
import Dropdown from "@/components/Dropdown/Dropdown";
import {
  MenuItemEnum,
  NotificationTypeEnum,
  SeverityEnum,
  NotificationTypeLabels,
  SeverityLabels,
} from "@/utils/utils";
import ShepDataGridPro from "@/components/shared/ShepDataGridPro";
import { importStatusEnum } from "@/features/maintenance/fileImportSlice";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  ShepCheckbox,
  ShepTooltip,
  ShepHorizontalMenu,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ProcessStatusSuccess,
  ProcessStatusError,
  ProcessStatusProcessing,
  ProcessStatusNotQueued,
} from "../../ImageRepo";
dayjs.extend(utc);

const Notifications = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [operations, setOperations] = useState([]);

  const gridHeaderSx = {
    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
      backgroundColor: "var(--primary-pale)",
      borderBottom: "none",
      borderLeft: "none",
    },
    color: "var(--text-primary)",
    fontSize: "var(--font-sm)",
    fontFamily: "var(--font-family)",
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
    backgroundColor: "var(--default-light)",
    border: "none",
    "& .MuiDataGrid:hover": {
      backgroundColor: "inherit",
    },
    "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
      opacity: 0,
      borderBottom: "none",
    },
    "& .MuiDataGrid-cell": {
      borderBottom: "none",
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "var(--tertiary-13)",
    },
    "& .MuiDataGrid-row.unread": {
      backgroundColor: "var(--tertiary-13)",
      fontWeight: 600,
    },
    "& .workflow-row:hover": {
      backgroundColor: "transparent",
    },
    "& .Mui-selected": {
      backgroundColor: "var(--primary-pale)",
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
    {
      outline: "none",
    },
    "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
    {
      opacity: 1,
    },
    "& .MuiDataGrid-cellCheckbox": {
      borderLeft: "8px solid transparent",
    },
    "& .Mui-selected .MuiDataGrid-cellCheckbox": {
      opacity: 1,
      borderLeft: "8px solid var(--primary-main)",
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontSize: "var(--font-md)",
      fontWeight: "var(--font-bold)",
      borderLeft: "none",
    },
    "& .MuiCheckbox-root": {
      display: "block",
    },
    "& .read-notification": {
      display: "flex",
      justifyContent: "center",
    },
  };

  const getNotificationTypeLabel = (type, operationId) => {
    if (type !== NotificationTypeEnum.Workflow) {
      return NotificationTypeLabels[type];
    } else {
      let typeLabel = "Workflow";
      const opName = operations.find(
        (op) => op.OperationId === operationId,
      )?.OperationName;
      if (opName) typeLabel += ` - ${opName}`;
      return typeLabel;
    }
  };

  const gridRef = useRef(null);
  const columns = [
    {
      field: "TimestampUTC",
      headerName: "Date / Time",
      width: 175,
      renderCell: (params) =>
        dayjs.utc(params.row.TimestampUTC).local().format("YYYY.MM.DD HH:mm"),
    },
    {
      field: "Summary",
      headerName: "Name",
      width: 150,
    },
    {
      field: "NotificationType",
      headerName: "Type",
      width: 400,
      renderCell: (params) => {
        return (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {getNotificationTypeLabel(params.value, params.row.OperationId)}
          </Box>
        );
      },
    },
    {
      field: "Severity",
      headerName: "Status",
      width: 200,
      renderCell: (params) => {
        return SeverityLabels[params.value];
      },
    },
    {
      field: "Message",
      headerName: "Message",
      width: 100,
      flex: 1,
    },
    {
      field: "Details",
      headerName: "Details",
      width: 100,
      flex: 1,
    },
    {
      field: "ReadStatus",
      headerName: "Read",
      width: 100,
      cellClassName: "read-notification",
      renderCell: (params) => {
        return (
          <ShepCheckbox
            onChange={() =>
              readNotification(
                params.row.NotificationLogId,
                params.row.NotificationConfigurationId,
              )
            }
            checked={params.value}
            disabled={params.value}
            edge="start"
          />
        );
      },
    },
  ];

  useEffect(() => {
    getOperations();
  }, []);

  //Notification functions-------//

  const [notificationGridLoaded, setNotificationGridLoaded] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  useEffect(() => {
    setNotificationGridLoaded(false);
    getNotifications();
  }, [showAllNotifications]);

  const toggleAllNotifications = () => {
    setShowAllNotifications((prev) => !prev);
  };

  const getNotifications = () => {
    const params = {
      viewAll: showAllNotifications,
    };
    axios
      .get("/notifications", { params })
      .then((response) => {
        const responseData = response.data;
        setNotificationGridLoaded(true);
        setRows(responseData);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const readAllNotifications = () => {
    axios
      .patch("/notifications/read/all")
      .then(() => {
        setNotificationGridLoaded(false);
        getNotifications();
      })
      .catch((error) => {
        console.error("Error marking notifications as read:", error);
      });
  };

  const readNotification = (notificationId, notificationConfigurationId) => {
    // Optimistically update notification and then roll back if error occurs
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.NotificationLogId === notificationId &&
          row.NotificationConfigurationId === notificationConfigurationId
          ? { ...row, ReadStatus: true }
          : row,
      ),
    );
    axios
      .patch("/notifications/read", {
        notificationId,
        notificationConfigurationId,
      })
      .catch((error) => {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.NotificationLogId === notificationId &&
              row.NotificationConfigurationId === notificationConfigurationId
              ? { ...row, ReadStatus: false }
              : row,
          ),
        );
        console.error(
          `Error marking notification with id ${notificationId} as read:`,
          error,
        );
      });
  };

  const getOperations = () => {
    axios
      .get("/connections/operations")
      .then((response) => {
        const responseData = response.data;
        setOperations(responseData ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  //File Import functions-------//
  const [importLogs, setImportLogs] = useState([]);
  const [importLogsLoaded, setImportLogsLoaded] = useState(false);

  const actions = [
    {
      label: "Go To Configurations",
      onClick: () => navigate("/notification-config"),
    },
    {
      label: `${showAllNotifications ? "Show Configured Notifications" : "Show All Notifications"}`,
      onClick: toggleAllNotifications,
    },
    {
      label: "Mark All As Read",
      onClick: readAllNotifications,
      disabled: showAllNotifications,
    },
  ];

  const importLogColumns = [
    { field: "FileName", headerName: "File Name", width: 250 },
    {
      field: "LogTimeUTC",
      headerName: "Date / Time",
      width: 180,
      renderCell: (params) =>
        dayjs.utc(params.row.LogTimeUTC).local().format("YYYY.MM.DD HH:mm"),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 265,
      renderCell: (params) => {
        let text = "";
        let icon = null;
        if (params.row.Status === importStatusEnum.Queued) {
          text = "Queued";
          icon = (
            <img
              alt="pending"
              src={ProcessStatusProcessing}
              style={{ height: 20, verticalAlign: "middle" }}
            />
          );
        } else if (params.row.Status === importStatusEnum.Completed) {
          text = "Completed";
          icon = (
            <img
              alt="success"
              src={ProcessStatusSuccess}
              style={{ height: 20, verticalAlign: "middle" }}
            />
          );
        } else if (
          params.row.Status === importStatusEnum.CompletedWithWarning
        ) {
          text = "Completed With Warning";
          icon = (
            <img
              alt="warning"
              src={ProcessStatusSuccess}
              style={{ height: 20, verticalAlign: "middle" }}
            />
          );
        } else if (params.row.Status === importStatusEnum.Processing) {
          text = "Processing";
          icon = (
            <img
              alt="pending"
              src={ProcessStatusProcessing}
              style={{ height: 20, verticalAlign: "middle" }}
            />
          );
        } else if (params.row.Status === importStatusEnum.Cancelled) {
          text = "Cancelled";
          icon = (
            <img
              alt="error"
              src={ProcessStatusNotQueued}
              style={{ height: 20, verticalAlign: "middle" }}
            />
          );
        } else {
          text = "Failed";
          icon = (
            <img
              alt="error"
              src={ProcessStatusError}
              style={{ height: 20, verticalAlign: "middle" }}
            />
          );
        }
        return (
          <span style={{ display: "flex", alignItems: "center" }}>
            {icon}
            <span style={{ marginLeft: 8 }}>{text}</span>
          </span>
        );
      },
    },
    {
      field: "progress",
      headerName: "Progress %",
      width: 180,

      renderCell: (params) => {
        const jsonDetails = params.row.JSONDetails ?? "{}";
        const details = JSON.parse(jsonDetails);

        if (params.row.Status === importStatusEnum.Processing) {
          return details.ProgressPercent + "%";
        }
        if (
          params.row.Status === importStatusEnum.Completed ||
          params.row.Status === importStatusEnum.CompletedWithWarning
        ) {
          return "100%";
        } else return "";
      },
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      width: 205,

      renderCell: (params) => {
        const jsonDetails = params.row.JSONDetails ?? "{}";
        const details = JSON.parse(jsonDetails);
        return (
          dayjs
            .utc(details.LastUpdatedUTC)
            .local()
            .format("YYYY.MM.DD HH:mm") ?? ""
        );
      },
    },
    {
      field: "message",
      headerName: "Message",
      width: 200,
      flex: 7,
      renderCell: (params) => {
        const jsonDetails = params.row.JSONDetails ?? "{}";
        const details = JSON.parse(jsonDetails);
        return details.Message ?? "";
      },
    },
    {
      field: "threedotMenu",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          label="Cancel"
          showInMenu
          disabled={
            params.row.Status !== importStatusEnum.Queued &&
            params.row.Status !== importStatusEnum.Processing
          }
          id="single-grid-action"
          onClick={() => handleCancelRecord({ params })}
        />,
      ],
    },
  ];
  useEffect(() => {
    fetchRecentFileImportLogs();
  }, []);
  useEffect(() => {
    const existsInProgress = importLogs.find(
      (x) => x.Status === importStatusEnum.Processing,
    );
    if (existsInProgress !== undefined) {
      const timer = setTimeout(() => {
        fetchRecentFileImportLogs();
      }, 120000); //2 mins
      return () => clearTimeout(timer);
    }
  }, [importLogs]);

  const fetchRecentFileImportLogs = async () => {
    await axios.get("/fileimport/logs").then((response) => {
      setImportLogs(response.data);
      setImportLogsLoaded(true);
    });
  };

  const handleCancelRecord = async ({ params }) => {
    const fileImportId = params.row.FileImportId;
    const fileName = params.row.FileName;
    await axios
      .post("/fileimport/cancel", null, {
        params: { fileImportId, fileName },
      })
      .then(() => {
        fetchRecentFileImportLogs();
      });
  };

  //---------------------------//

  return (
    <Container>
      <MainNavigation selected={MenuItemEnum.Notifications} />

      <Box
        className="content-container"
        style={{
          boxSizing: "border-box",
        }}
      >
        <Box style={{ display: "flex", justifyContent: "flex-end" }}>
          <ShepHorizontalMenu actions={actions} />
        </Box>

        <Container
          sx={{
            height: "78px",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            padding: "20px 0px 20px 40px",
            borderRadius: "4px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            marginTop: "24px",
          }}
        >
          <Typography variant="h4">Notifications</Typography>
        </Container>
        <ShepDataGridPro
          isDataLoaded={notificationGridLoaded}
          ref={gridRef}
          rows={rows}
          columns={columns}
          pagination
          getRowClassName={(params) => {
            return !showAllNotifications && !params.row.ReadStatus
              ? "unread"
              : "";
          }}
          slots={{
            pagination: ShepPagination,
            baseCheckbox: ShepCheckbox,
            baseTooltip: ShepTooltip,
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 15, page: 0 },
            },
          }}
          columnVisibilityModel={{
            ReadStatus: !showAllNotifications,
          }}
          sx={gridHeaderSx}
        />
        <Box sx={{ marginTop: "50px" }}>
          <Container
            sx={{
              height: "78px",
              backgroundColor: "var(--primary-main)",
              color: "var(--default-light)",
              padding: "20px 0px 20px 40px",
              borderRadius: "4px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              marginTop: "24px",
            }}
          >
            <Typography variant="h4">File Import Status</Typography>
          </Container>
          <ShepDataGridPro
            isDataLoaded={importLogsLoaded}
            secondsToStall={4}
            rows={importLogs}
            columns={importLogColumns}
            getRowId={(row) => row.FileImportLogId}
            pagination
            slots={{
              pagination: ShepPagination,
              baseCheckbox: ShepCheckbox,
              baseTooltip: ShepTooltip,
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 15, page: 0 },
              },
            }}
            sx={gridHeaderSx}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Notifications;
